import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
  Button, Input, Badge
} from '../../components/UI';
import MainLayout from '../Layout/MainLayout';
import apiClient from '../../utils/api';
import {
  ArrowLeft, ArrowRight, Save, Sparkles, Building2,
  BarChart3, RefreshCw, CheckCircle, AlertCircle,
  Loader2, DollarSign, TrendingUp, Edit3, Check
} from 'lucide-react';

const STEPS = [
  { id: 1, name: 'Setup', description: 'Configure budget parameters' },
  { id: 2, name: 'Scan Data', description: 'Fetch NetSuite data' },
  { id: 3, name: 'Data Quality', description: 'Review tagging metrics' },
  { id: 4, name: 'Categorize', description: 'AI-powered categorization' },
  { id: 5, name: 'Suggestions', description: 'AI budget recommendations' },
  { id: 6, name: 'Review & Save', description: 'Finalize budget' }
];

const BudgetAgentNew = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Step 1: Setup
  const [budgetName, setBudgetName] = useState('');
  const [fiscalYear, setFiscalYear] = useState('2026');
  const [subsidiaryId, setSubsidiaryId] = useState('');
  const [historyMonths, setHistoryMonths] = useState(24);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [loadingSubsidiaries, setLoadingSubsidiaries] = useState(false);

  // Step 2: Scan Data
  const [scanData, setScanData] = useState(null);

  // Step 3: Data Quality
  const [dataQuality, setDataQuality] = useState(null);

  // Step 4: Categories
  const [categories, setCategories] = useState([]);

  // Step 5: Suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [editingSuggestion, setEditingSuggestion] = useState(null);

  // Load subsidiaries on mount
  useEffect(() => {
    fetchSubsidiaries();
  }, []);

  const fetchSubsidiaries = async () => {
    try {
      setLoadingSubsidiaries(true);
      const response = await apiClient.get('/api/budget-agent/subsidiaries');
      if (response && response.subsidiaries) {
        setSubsidiaries(response.subsidiaries);
      }
    } catch (err) {
      console.error('Error fetching subsidiaries:', err);
    } finally {
      setLoadingSubsidiaries(false);
    }
  };

  const handleScanData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.post('/api/budget-agent/scan', {
        subsidiary_id: subsidiaryId || null,
        months: historyMonths
      });

      if (response && response.success) {
        setScanData(response.data);
        setDataQuality(response.data.data_quality);
        setCurrentStep(3);
      } else {
        setError(response?.error || 'Failed to scan data');
      }
    } catch (err) {
      setError(err.message || 'Failed to scan NetSuite data');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorize = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.post('/api/budget-agent/categorize', {
        accounts: scanData?.accounts || []
      });

      if (response && response.success) {
        setCategories(response.data.categories || []);
        setCurrentStep(4);
      } else {
        setError(response?.error || 'Failed to categorize accounts');
      }
    } catch (err) {
      setError(err.message || 'Failed to categorize accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSuggestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.post('/api/budget-agent/suggest', {
        categories: categories,
        historical_data: scanData?.transaction_summary || {}
      });

      if (response && response.success) {
        setSuggestions(response.data.suggestions || []);
        setCurrentStep(5);
      } else {
        setError(response?.error || 'Failed to generate suggestions');
      }
    } catch (err) {
      setError(err.message || 'Failed to generate suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBudget = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.post('/api/budget-agent/save', {
        budget_name: budgetName,
        fiscal_year: fiscalYear,
        subsidiary_id: subsidiaryId || null,
        categories: suggestions
      });

      if (response && response.success) {
        navigate('/budget-agent');
      } else {
        setError(response?.error || 'Failed to save budget');
      }
    } catch (err) {
      setError(err.message || 'Failed to save budget');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return '-';
    return `${(value * 100).toFixed(1)}%`;
  };

  // Step Navigation
  const StepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm
                  ${currentStep > step.id
                    ? 'bg-green-500 text-white'
                    : currentStep === step.id
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                  }`}
              >
                {currentStep > step.id ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  step.id
                )}
              </div>
              <span className={`mt-2 text-xs font-medium
                ${currentStep >= step.id
                  ? 'text-indigo-600 dark:text-indigo-400'
                  : 'text-gray-500'
                }`}>
                {step.name}
              </span>
            </div>
            {index < STEPS.length - 1 && (
              <div className={`flex-1 h-1 mx-2
                ${currentStep > step.id
                  ? 'bg-green-500'
                  : 'bg-gray-200 dark:bg-gray-700'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  // Step 1: Setup Form
  const SetupStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Budget Configuration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">Budget Name</label>
          <Input
            value={budgetName}
            onChange={(e) => setBudgetName(e.target.value)}
            placeholder="FY2026 Operating Budget"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Fiscal Year</label>
            <select
              value={fiscalYear}
              onChange={(e) => setFiscalYear(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="2024">2024</option>
              <option value="2025">2025</option>
              <option value="2026">2026</option>
              <option value="2027">2027</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">History Months</label>
            <Input
              type="number"
              value={historyMonths}
              onChange={(e) => setHistoryMonths(parseInt(e.target.value) || 24)}
              min={6}
              max={60}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Subsidiary (Optional)</label>
          <select
            value={subsidiaryId}
            onChange={(e) => setSubsidiaryId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="">All Subsidiaries</option>
            {subsidiaries.map((sub) => (
              <option key={sub.id} value={sub.id}>
                {sub.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={() => setCurrentStep(2)}
            disabled={!budgetName}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            Next: Scan Data
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Step 2: Scan Data
  const ScanStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="w-5 h-5" />
          Scan NetSuite Data
        </CardTitle>
      </CardHeader>
      <CardContent className="text-center py-12">
        {loading ? (
          <div className="space-y-4">
            <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mx-auto" />
            <p className="text-gray-600 dark:text-gray-400">
              Scanning NetSuite for expense accounts and transaction history...
            </p>
            <p className="text-sm text-gray-500">This may take a moment</p>
          </div>
        ) : (
          <div className="space-y-4">
            <BarChart3 className="w-12 h-12 text-indigo-500 mx-auto" />
            <h3 className="text-lg font-semibold">Ready to Scan</h3>
            <p className="text-gray-600 dark:text-gray-400">
              We'll fetch expense accounts, subsidiaries, departments, and {historyMonths} months of transaction history.
            </p>
            <div className="flex justify-center gap-4 mt-6">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(1)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleScanData}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Start Scan
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Step 3: Data Quality
  const DataQualityStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Data Quality Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        {dataQuality && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-500">Total Transactions</p>
                <p className="text-2xl font-bold">{dataQuality.total_transactions || 0}</p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-500">Subsidiary Tagged</p>
                <p className="text-2xl font-bold">{formatPercent(dataQuality.subsidiary_tagged)}</p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-500">Department Tagged</p>
                <p className="text-2xl font-bold">{formatPercent(dataQuality.department_tagged)}</p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-500">Class Tagged</p>
                <p className="text-2xl font-bold">{formatPercent(dataQuality.class_tagged)}</p>
              </div>
            </div>

            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Accounts found:</strong> {scanData?.accounts?.length || 0} expense accounts
              </p>
            </div>

            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setCurrentStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleCategorize}
                disabled={loading}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Categorize with AI
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Step 4: Categories Review
  const CategoriesStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-indigo-500" />
          AI-Generated Categories
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mx-auto" />
            <p className="mt-4 text-gray-600">AI is analyzing and categorizing accounts...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {categories.map((category, index) => (
              <div
                key={category.category_id || index}
                className="p-4 border rounded-lg dark:border-gray-700"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold">{category.category_name}</h4>
                    <p className="text-sm text-gray-500">
                      {category.accounts?.length || 0} accounts
                    </p>
                  </div>
                  <Badge variant="outline">
                    {category.suggested_slicing?.join(', ') || 'No slicing'}
                  </Badge>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {category.accounts?.slice(0, 5).map((acc, i) => (
                    <span
                      key={i}
                      className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded"
                    >
                      {acc.name || acc.number}
                    </span>
                  ))}
                  {category.accounts?.length > 5 && (
                    <span className="text-xs text-gray-500">
                      +{category.accounts.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            ))}

            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setCurrentStep(3)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleGenerateSuggestions}
                disabled={loading || categories.length === 0}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <TrendingUp className="w-4 h-4 mr-2" />
                )}
                Generate Budget Suggestions
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Step 5: Suggestions Review
  const SuggestionsStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-500" />
          Budget Suggestions
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mx-auto" />
            <p className="mt-4 text-gray-600">AI is generating budget recommendations...</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b dark:border-gray-700">
                    <th className="text-left py-3 px-4">Category</th>
                    <th className="text-right py-3 px-4">Suggested Budget</th>
                    <th className="text-right py-3 px-4">Growth</th>
                    <th className="text-center py-3 px-4">Confidence</th>
                    <th className="text-left py-3 px-4">Reasoning</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.map((suggestion, index) => (
                    <tr key={index} className="border-b dark:border-gray-700">
                      <td className="py-3 px-4 font-medium">
                        {suggestion.category_name || suggestion.category_id}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {editingSuggestion === index ? (
                          <Input
                            type="number"
                            value={suggestion.suggested_budget}
                            onChange={(e) => {
                              const newSuggestions = [...suggestions];
                              newSuggestions[index].suggested_budget = parseFloat(e.target.value) || 0;
                              setSuggestions(newSuggestions);
                            }}
                            className="w-32 text-right"
                          />
                        ) : (
                          formatCurrency(suggestion.suggested_budget)
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={suggestion.growth_rate > 0 ? 'text-green-500' : 'text-red-500'}>
                          {suggestion.growth_rate > 0 ? '+' : ''}{formatPercent(suggestion.growth_rate)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge
                          className={
                            suggestion.confidence === 'high'
                              ? 'bg-green-500'
                              : suggestion.confidence === 'medium'
                                ? 'bg-yellow-500'
                                : 'bg-gray-500'
                          }
                        >
                          {suggestion.confidence}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                        {suggestion.reasoning}
                      </td>
                      <td className="py-3 px-4">
                        {editingSuggestion === index ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingSuggestion(null)}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingSuggestion(index)}
                          >
                            <Edit3 className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-bold">
                    <td className="py-3 px-4">Total</td>
                    <td className="py-3 px-4 text-right">
                      {formatCurrency(suggestions.reduce((sum, s) => sum + (s.suggested_budget || 0), 0))}
                    </td>
                    <td colSpan={4}></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setCurrentStep(4)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={() => setCurrentStep(6)}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Review & Save
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Step 6: Review & Save
  const ReviewStep = () => {
    const totalBudget = suggestions.reduce((sum, s) => sum + (s.suggested_budget || 0), 0);

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            Review & Save Budget
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-500">Budget Name</p>
                <p className="text-lg font-semibold">{budgetName}</p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-500">Fiscal Year</p>
                <p className="text-lg font-semibold">{fiscalYear}</p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-500">Categories</p>
                <p className="text-lg font-semibold">{suggestions.length}</p>
              </div>
              <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                <p className="text-sm text-indigo-600 dark:text-indigo-400">Total Budget</p>
                <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {formatCurrency(totalBudget)}
                </p>
              </div>
            </div>

            {error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <span className="text-red-600 dark:text-red-400">{error}</span>
              </div>
            )}

            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setCurrentStep(5)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleSaveBudget}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Budget
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <SetupStep />;
      case 2:
        return <ScanStep />;
      case 3:
        return <DataQualityStep />;
      case 4:
        return <CategoriesStep />;
      case 5:
        return <SuggestionsStep />;
      case 6:
        return <ReviewStep />;
      default:
        return <SetupStep />;
    }
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/budget-agent')}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Create New Budget
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              AI-powered budget creation wizard
            </p>
          </div>
        </div>

        {/* Step Indicator */}
        <StepIndicator />

        {/* Error Display */}
        {error && currentStep !== 6 && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-600 dark:text-red-400">{error}</span>
          </div>
        )}

        {/* Current Step Content */}
        {renderStep()}
      </div>
    </MainLayout>
  );
};

export default BudgetAgentNew;
